import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import {
  Alert,
  backLinkClasses,
  Badge,
  Button,
  Card,
  DataList,
  DataRow,
  Icon,
  linkTextClasses,
  NotProvided,
  Page,
  PageHeader,
  rowLinkClasses,
  Table,
  isNotFound,
  NotFoundError,
} from '@evogard/ui';
import { canAccess, formatDate, loadFailureMessage } from '@evogard/domain';
import { MoneyValue } from '../../components/Values.js';
import { backSearch, useTablePage } from '../../lib/list-search.js';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import {
  MirrorStamp,
  PaidValue,
  RefreshOutcome,
  StatusBadge,
  useErpRefresh,
} from './common.js';
import { ChangesCard } from './history.js';
import { MissingRecord } from '../../components/MissingRecord.js';

/**
 * A ficha de um boleto, aberta pela seta da lista.
 *
 * ## O endereço é o código da casa
 *
 * `BO-000123`, e nunca o número impresso nem o código do ERP: os dois aparecem
 * na ficha como dado, e a regra 7 reserva a identidade para o `code`.
 *
 * ## Situação e "pago" continuam dois campos
 *
 * Pelo motivo da lista: a CE tem boleto baixado que o ERP não considera pago,
 * e uma linha só obrigaria a ficha a escolher entre duas mentiras.
 *
 * ## Os veículos são os que o ERP LIGOU ao boleto
 *
 * E não os do associado: quem tem três carros veria os três aqui, e o boleto
 * cobre os que a ligação diz. Lista vazia é dita como lista vazia.
 *
 * ## Tudo o que o ERP devolveu, em blocos
 *
 * O boleto, como pagar, o pagamento, a cobrança, o banco e o associado. Campo
 * vazio aparece como "não informado", e não some: a ficha é o lugar de ver
 * que o ERP não mandou. O link só vira link quando é `http(s)` — o valor é do
 * fornecedor, e um `javascript:` num `href` não é um endereço.
 *
 * ## O link e a linha digitável só vêm pelo "Atualizar do ERP"
 *
 * A varredura em massa (`/listar/boleto`) não os devolve; quem devolve é a
 * busca de UM boleto (`/buscar/boleto/:nosso_numero`). Medido em 13/10/2026 na
 * CE: 67 boletos com link em 141.643 — os que alguém pediu um a um. Por isso
 * a ficha tem o botão, e o bloco "Como pagar" diz de onde o dado vem quando
 * ele falta.
 */
export function BoletoScreen() {
  const { code } = useParams({ from: '/authenticated/erp/boletos/$code' });
  /* a página de cada tabela desta ficha, na URL — o "voltar" devolve a página */
  const pages = useSearch({ from: '/authenticated/erp/boletos/$code' });
  const vehiclesPage = useTablePage('veiculos', pages.veiculos);
  const alteracoesPage = useTablePage('alteracoes', pages.alteracoes);
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  /* pedir atualização vai ao ERP — a mesma separação da ficha do veículo */
  const canRefresh = canAccess(context?.permissions ?? [], 'erp.sync', 'executar');

  /**
   * Pede ESTE boleto ao ERP, pelo `codigo_boleto`. A rota espera o job, que
   * grava o espelho e projeta o núcleo — o `200` relê a ficha já nova.
   */
  const queryKey = ['erp', 'boleto', tenantId, code] as const;
  const { mutation: refresh, follow } = useErpRefresh(queryKey, () =>
    b && b.erpCode !== null ? { entity: 'boleto', id: b.erpCode } : null,
  );

  const query = useQuery({
    refetchInterval: follow,
    queryKey,
    queryFn: async () => {
      const r = await api.erp.boleto({ params: { code } });
      if (r.status === 404) {
        throw new NotFoundError('Este boleto não existe no cadastro desta base');
      }
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'o boleto'));
      }
      return r.body;
    },
    retry: false,
  });

  const b = query.data;

  /*
   * O link e a linha digitável só vêm na busca de UM boleto. Quem abre a ficha
   * de um boleto que nunca foi pedido assim dispara o pedido uma vez, sozinho —
   * uma vez por boleto aberto, e nunca de novo na mesma visita, porque o ERP
   * pode simplesmente não ter link para dar.
   */
  const asked = useRef<string | null>(null);
  const missingLink = b !== undefined && b.erp.link === null && b.erpCode !== null;
  const { mutate: requestRefresh } = refresh;
  useEffect(() => {
    if (!missingLink || !canRefresh || asked.current === code) return;
    asked.current = code;
    requestRefresh();
  }, [missingLink, canRefresh, code, requestRefresh]);

  if (isNotFound(query.error)) {
    return (
      <MissingRecord
        title={query.error.message}
        list={{ to: '/erp/boletos', label: 'Ver os boletos' }}
      />
    );
  }

  return (
    <Page measure="cheia">
      <Link
        to="/erp/boletos"
        search={backSearch('/erp/boletos') as never}
        className={backLinkClasses()}
      >
        Boletos
      </Link>

      <PageHeader title={`Boleto ${code}`}>
        Um boleto de {context?.currentTenant.nome}, como o ERP o entrega.
      </PageHeader>

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {b && canRefresh && b.erpCode !== null && (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            icon="atualizar"
          >
            {refresh.isPending ? 'Atualizando…' : 'Atualizar do ERP'}
          </Button>
          <span className="text-sm text-texto-suave">
            traz este boleto inteiro, com o link, a linha digitável e os veículos dele
          </span>
        </div>
      )}

      <RefreshOutcome mutation={refresh} />

      {b && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="grid content-start gap-4">
            <Card title="O boleto" descricao={<MirrorStamp at={b.syncedAt} />}>
              <DataList>
                <DataRow label="Situação">
                  <StatusBadge situacao={b.situacao} />
                </DataRow>
                <DataRow label="Valor">
                  <MoneyValue value={b.value} />
                </DataRow>
                <DataRow label="Vencimento">
                  {b.dueDate ? formatDate(b.dueDate) : <NotProvided />}
                </DataRow>
                {/* as duas datas: com uma só, prorrogar zera o atraso sem avisar */}
                <DataRow label="Vencimento original">
                  {b.dueDateOriginal ? formatDate(b.dueDateOriginal) : <NotProvided />}
                </DataRow>
                <DataRow label="Emissão">
                  {b.erp.issuedOn ? formatDate(b.erp.issuedOn) : <NotProvided />}
                </DataRow>
                <DataRow label="Competência">{b.period ?? <NotProvided />}</DataRow>
                <DataRow label="Tipo de boleto">
                  <CodeAndLabel code={b.erp.typeCode} label={b.erp.typeLabel} />
                </DataRow>
                <DataRow label="Nosso número">
                  {b.nossoNumero ? (
                    <span className="font-mono text-xs">{b.nossoNumero}</span>
                  ) : (
                    <NotProvided />
                  )}
                </DataRow>
                <DataRow label="Código">{b.code}</DataRow>
                <DataRow label="Código no ERP">
                  {b.erpCode ?? <NotProvided>não veio do ERP</NotProvided>}
                </DataRow>
              </DataList>
            </Card>

            <Card
              title="Como pagar"
              descricao="O link e a linha digitável só vêm quando o boleto é pedido ao ERP um a um."
            >
              <DataList>
                <DataRow label="Link do boleto">
                  {b.erp.link === null ? (
                    <NotProvided>ainda não pedido ao ERP</NotProvided>
                  ) : /^https?:\/\//i.test(b.erp.link) ? (
                    <a
                      href={b.erp.link}
                      target="_blank"
                      rel="noreferrer"
                      className={linkTextClasses('break-all')}
                    >
                      Abrir o boleto
                    </a>
                  ) : (
                    <span className="break-all font-mono text-xs">{b.erp.link}</span>
                  )}
                </DataRow>
                <DataRow label="Linha digitável">
                  {b.erp.digitableLine ? (
                    <span className="break-all font-mono text-xs">
                      {b.erp.digitableLine}
                    </span>
                  ) : (
                    <NotProvided>ainda não pedida ao ERP</NotProvided>
                  )}
                </DataRow>
              </DataList>
            </Card>

            <Card title="A cobrança">
              <DataList>
                <DataRow label="Cobrança recorrente">
                  {b.erp.recurringBillingTypeLabel ?? <NotProvided />}
                </DataRow>
                <DataRow label="Parcela paga">
                  {b.erp.installmentPaid ?? <NotProvided />}
                </DataRow>
                <DataRow label="Quantidade de parcelas">
                  {b.erp.installmentCount ?? <NotProvided />}
                </DataRow>
                <DataRow label="Regional">
                  {b.erp.regionalCode ?? <NotProvided />}
                </DataRow>
              </DataList>
            </Card>
          </div>

          <div className="grid content-start gap-4">
            <Card title="O pagamento">
              <DataList>
                <DataRow label="O ERP considera pago">
                  {b.pago === null ? (
                    <NotProvided>o ERP não disse</NotProvided>
                  ) : (
                    <Badge tone={b.pago ? 'sucesso' : 'neutro'}>
                      {b.pago ? 'sim' : 'não'}
                    </Badge>
                  )}
                </DataRow>
                <DataRow label="Pago em">
                  {b.paidAt ? (
                    formatDate(b.paidAt)
                  ) : (
                    <NotProvided>sem pagamento</NotProvided>
                  )}
                </DataRow>
                <DataRow label="Valor pago">
                  <PaidValue value={b.valuePaid} source={b.paymentSource} />
                </DataRow>
                <DataRow label="Forma de pagamento">
                  <CodeAndLabel
                    code={b.erp.paymentMethodCode}
                    label={b.erp.paymentMethodLabel}
                  />
                </DataRow>
                <DataRow label="Código MGF da forma de pagamento">
                  {b.erp.mgfPaymentMethodCode ?? <NotProvided />}
                </DataRow>
                <DataRow label="Tipo de baixa">
                  {b.erp.settlementTypeLabel ?? <NotProvided />}
                </DataRow>
                <DataRow label="Crédito no banco">
                  {b.erp.bankCreditDate ? (
                    formatDate(b.erp.bankCreditDate)
                  ) : (
                    <NotProvided />
                  )}
                </DataRow>
                <DataRow label="Tarifa do banco">
                  <MoneyValue value={b.erp.bankFee} />
                </DataRow>
                {/* os do ERP, crus: os de cima podem ser os da coleta */}
                <DataRow label="Pago em, segundo o ERP">
                  {b.erp.paidAt ? formatDate(b.erp.paidAt) : <NotProvided />}
                </DataRow>
                <DataRow label="Valor pago, segundo o ERP">
                  <MoneyValue value={b.erp.paidAmount} />
                </DataRow>
              </DataList>
            </Card>

            <Card title="O banco">
              <DataList>
                <DataRow label="Banco">
                  <CodeAndLabel code={b.erp.bankCode} label={b.erp.bankName} />
                </DataRow>
                <DataRow label="Agência">{b.erp.agency ?? <NotProvided />}</DataRow>
                <DataRow label="Conta">{b.erp.account ?? <NotProvided />}</DataRow>
                <DataRow label="Código da conta">
                  {b.erp.accountCode ?? <NotProvided />}
                </DataRow>
              </DataList>
            </Card>

            <Card title="O associado">
              {b.member === null ? (
                <p className="px-4 py-8 text-center text-sm text-texto-suave">
                  Este boleto não aponta associado no cadastro do ERP.
                </p>
              ) : (
                <DataList>
                  <DataRow label="Nome">{b.member.nome ?? <NotProvided />}</DataRow>
                  <DataRow label="Código no ERP">{b.member.code}</DataRow>
                  <DataRow label="Ficha">
                    <Link
                      to="/erp/associados/$code"
                      params={{ code: b.member.code }}
                      className={linkTextClasses()}
                    >
                      Abrir a ficha do associado
                    </Link>
                  </DataRow>
                </DataList>
              )}
            </Card>
          </div>
        </div>
      )}

      {b && (
        <Card title={`Veículos deste boleto (${b.vehicles.length})`} bleed>
          <Table
            {...vehiclesPage}
            label="Veículos deste boleto"
            items={b.vehicles}
            rowKey={(v) => v.code}
            empty="O ERP não ligou este boleto a nenhum veículo do cadastro."
            rowLink={(v) => (
              <Link
                to="/erp/veiculos/$code"
                params={{ code: v.code }}
                aria-label={`Abrir ${v.placa ?? `o veículo ${v.code}`}`}
                className={rowLinkClasses}
              >
                <Icon nome="seta-direita" size={16} />
              </Link>
            )}
            columns={[
              {
                title: 'Placa',
                minWidth: '7rem',
                cell: (v) => v.placa ?? <NotProvided>sem placa</NotProvided>,
              },
              {
                title: 'Veículo',
                alignment: 'inicio' as const,
                minWidth: '14rem',
                cell: (v) =>
                  [v.marca, v.modelo].filter(Boolean).join(' ') || (
                    <NotProvided>não informado</NotProvided>
                  ),
              },
              {
                title: 'Situação do veículo',
                minWidth: '11rem',
                cell: (v) => <StatusBadge situacao={v.situacao} />,
              },
            ]}
          />
        </Card>
      )}

      {b && <ChangesCard changes={b.changes} of="neste boleto" paging={alteracoesPage} />}
    </Page>
  );
}

/** A descrição com o código ao lado; só o código quando o ERP não descreve. */
function CodeAndLabel({ code, label }: { code: string | null; label: string | null }) {
  if (code === null && label === null) return <NotProvided />;
  if (label === null) return <>{code}</>;
  return (
    <>
      {label}
      {code !== null && <span className="text-texto-suave"> ({code})</span>}
    </>
  );
}
